import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuthStore } from "./store/authStore";
import { useAuth } from "./hooks/useAuth";

import LandingPage from "./components/landing/LandingPage";
import AdminPasswordGate from "./components/auth/AdminPasswordGate";
import AppShell from "./components/layout/AppShell";
import FileExplorer from "./components/explorer/FileExplorer";
import TrashView from "./components/trash/TrashView";
import AdminDashboard from "./components/admin/AdminDashboard";
import AdminUserFileExplorer from "./components/admin/AdminUserFileExplorer";
import AdminUserDeletedFiles from "./components/admin/AdminUserDeletedFiles";

const Spinner = () => (
  <div className="flex h-screen items-center justify-center bg-gray-950">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-700 border-t-indigo-500" />
  </div>
);

const ServerOfflinePage = () => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-gray-950 p-6 text-center">
    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-800 mb-6">
      <svg className="h-8 w-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1012.728 12.728M5.636 5.636A9 9 0 0118.364 18.364M5.636 5.636L18.364 18.364" />
      </svg>
    </div>
    <h1 className="text-2xl font-bold text-white mb-2">Server Unavailable</h1>
    <p className="text-gray-400 max-w-sm mb-8">
      The backend server is currently offline or unreachable. Please try again in a moment.
    </p>
    <button
      onClick={() => window.location.reload()}
      className="rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-500 transition-colors"
    >
      Try again
    </button>
  </div>
);

/** Bootstraps auth from the session cookie on first render */
function AuthProvider({ children }: { children: React.ReactNode }) {
  const { isBackendDown } = useAuth(); // hydrates authStore — single source of truth
  if (isBackendDown) return <ServerOfflinePage />;
  return <>{children}</>;
}

/** Redirect logged-in users away from the login page */
function GuestRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();
  if (isLoading) return <Spinner />;
  if (!user) return <>{children}</>;
  return <Navigate to="/app" replace />;
}

/** Only APPROVED users */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();
  if (isLoading) return <Spinner />;
  if (!user) return <Navigate to="/" replace />;
  if (user.status === "REVOKED") return <Navigate to="/" replace />;
  if (user.adminPasswordStatus === "SETUP_REQUIRED") return <AdminPasswordGate mode="setup" />;
  if (user.adminPasswordStatus === "VERIFY_REQUIRED") return <AdminPasswordGate mode="verify" />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (user?.role !== "ADMIN") return <Navigate to="/app" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          classNames: {
            toast: "bg-gray-900 border border-gray-700 text-gray-100 text-sm rounded-xl shadow-xl",
            title: "text-gray-100 font-medium",
            description: "text-gray-400",
            success: "border-emerald-700/60",
            error: "border-red-700/60",
          },
        }}
      />
      <AuthProvider>
        <Routes>
          <Route path="/" element={<GuestRoute><LandingPage /></GuestRoute>} />
          {/* /login kept for backwards compatibility, redirects to / */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<FileExplorer />} />
            <Route path="folder/:folderId" element={<FileExplorer />} />
            <Route path="trash" element={<TrashView />} />
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="admin/users/:userId/files"
              element={
                <AdminRoute>
                  <AdminUserFileExplorer />
                </AdminRoute>
              }
            />
            <Route
              path="admin/users/:userId/folder/:folderId"
              element={
                <AdminRoute>
                  <AdminUserFileExplorer />
                </AdminRoute>
              }
            />
            <Route
              path="admin/users/:userId/deleted-files"
              element={
                <AdminRoute>
                  <AdminUserDeletedFiles />
                </AdminRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
